import { Mine__factory, PearlStore__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import { Tokens } from '~/utils/constants';
import { parseUnits } from 'ethers';
import type { ContractCallParams } from '~/types/Web3Provider';
import { useWeb3ModalAccount, useWeb3ModalProvider } from '@web3modal/ethers/vue';
import { useEnvs, useMainStore } from '#imports';

type Web3Confirmation = (confirmations?: number) => void;

export default () => {
  const { address } = useWeb3ModalAccount();
  const { sendContractMethod } = useSendContractMethod();
  const { blockchain } = useEnvs();
  const store = useMainStore();
  const { walletProvider } = useWeb3ModalProvider();
  const { getSigner } = useProvider();

  const sendContractMethodExtended = async <T extends Record<any, any>, M extends keyof T>(
    sendParams: ContractCallParams<T, M>,
    onConfirmation?: (confirmations?: number) => void
  ) => {
    const from = address.value;
    return sendContractMethod({ ...sendParams, from }, onConfirmation);
  };

  const claimMinedTokens = async (onConfirmation?: Web3Confirmation, tokenName: Tokens = store.currentToken) => {
    return sendContractMethodExtended(
      {
        contract: Mine__factory.connect(blockchain.contracts[tokenName].addresses.mine || '', await getSigner()),
        methodName: 'claim',
        args: []
      },
      onConfirmation
    );
  };

  /**
   * buyLicense
   * @param value - human-readable value like 0.1 USDT
   * @param tokenName - name of the token to buy
   * @param onConfirmation - confirmation callback, fired on each head block
   */
  const buyLicense = async (value: string, tokenName: Tokens, onConfirmation?: Web3Confirmation) => {
    const amount = parseUnits(value, 'ether');

    return sendContractMethodExtended(
      {
        contract: Mine__factory.connect(blockchain.contracts[tokenName].addresses.mine!, await getSigner()),
        methodName: 'buyLicense',
        args: [amount]
      },
      onConfirmation
    );
  };

  const buyLicenseAndRegisterInReferralProgram = async (
    value: string,
    tokenName: Tokens,
    referrerAddress: string,
    onConfirmation?: Web3Confirmation
  ) => {
    const amount = parseUnits(value, 'ether');

    if (!walletProvider.value) throw new Error('Wallet provider is required.');

    const signer = await getSigner();

    const contractAddress = blockchain.contracts[tokenName].addresses.mine;

    if (!contractAddress) throw new Error('Frontend misconfiguration: Mine contract address is required.');

    const storeContract = Mine__factory.connect(contractAddress, signer);
    const iface = storeContract.interface;

    const multiCallParams: string[] = [iface.encodeFunctionData('registerReferral', [referrerAddress])];

    if (tokenName === Tokens.pearl) {
      const pearlStoreIface = PearlStore__factory.createInterface();
      multiCallParams.push(pearlStoreIface.encodeFunctionData('buyPearlPack', [amount]));
    } else {
      multiCallParams.push(iface.encodeFunctionData('buyLicense', [amount]));
    }

    return sendContractMethodExtended(
      {
        contract: Mine__factory.connect(contractAddress, await getSigner()),
        methodName: 'multicall',
        args: [multiCallParams]
      },
      onConfirmation
    );
  };

  return {
    buyLicense,
    buyLicenseAndRegisterInReferralProgram,
    claimMinedTokens
  };
};
